import { useState, useRef, useCallback, useEffect } from 'react';
import { speech2Recognition } from '../../utils';

// 重采样器
class Resampler {
    constructor(fromSampleRate, toSampleRate, channels, outputBufferSize) {
        this.fromSampleRate = fromSampleRate;
        this.toSampleRate = toSampleRate;
        this.channels = channels;
        this.outputBufferSize = outputBufferSize;
        this.ratioWeight = fromSampleRate / toSampleRate;
        this.lastWeight = 0;
    }

    resample(inputBuffer) {
        const inputLength = inputBuffer.length;
        const outputLength = Math.floor(inputLength / this.ratioWeight);
        const output = new Float32Array(outputLength);
        
        let outputIndex = 0;
        let inputIndex = 0;
        let weight = this.lastWeight;
        
        while (outputIndex < outputLength) {
            const a = inputBuffer[Math.floor(inputIndex)];
            const b = inputBuffer[Math.floor(inputIndex) + 1] || 0;
            
            output[outputIndex] = a + (b - a) * (inputIndex - Math.floor(inputIndex));
            
            weight += this.ratioWeight;
            inputIndex = Math.floor(weight);
            outputIndex++;
        }
        
        this.lastWeight = weight - inputIndex;
        return output;
    }
}

// 服务器配置
const PROD_SERVER_API = "https://ai.eliteu.cn";
const DEV_SERVER_API = "https://aitest.eliteu.cn";
const serverAPI = window.location.host.indexOf("aimaker.space") > -1 
    ? PROD_SERVER_API 
    : DEV_SERVER_API;

const BUFSIZE = 8192;

/**
 * 实时语音识别Hook
 * @param {Object} options 配置选项
 * @param {number} options.timeout 超时时间（秒），默认100秒
 * @param {Function} options.onResult 识别结果回调
 * @param {Function} options.onError 错误回调
 */
export const useRealTimeAudioRecording = ({
    onResult,
    onError,
    onThen = () => {},
    onCatch = () => {},
    onVolumeChange,  // 新增音量变化回调
} = {}) => {
    const [isListening, setIsListening] = useState(false);
    const [error, setError] = useState(null);
    
    // 引用
    const audioContextRef = useRef(null);
    const resamplerRef = useRef(null);
    const sourceNodeRef = useRef(null);
    const scriptNodeRef = useRef(null);
    const wsRef = useRef(null);
    const micStreamRef = useRef(null);
    const bufferArrayRef = useRef([]);
    const connectSocketRef = useRef(false);
    const timeoutRef = useRef(null);
    const isListeningRef = useRef(false);
    const _lan = useRef(null)
    const _during = useRef(null)
    const isAvailable = useRef(true)
    
    // 同步 isListening 状态到 ref
    useEffect(() => {
        isListeningRef.current = isListening;
    }, [isListening]);
  
    
    // 初始化音频上下文
    const initAudioContext = useCallback(async () => {
        try {
            // 如果音频上下文已存在且未关闭，先关闭它
            if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                await audioContextRef.current.close();
            }
            
            // 创建新的音频上下文
            audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
            
            // 如果音频上下文被暂停，恢复它
            if (audioContextRef.current.state === 'suspended') {
                await audioContextRef.current.resume();
            }
            
            // 创建新的重采样器
            resamplerRef.current = new Resampler(
                audioContextRef.current.sampleRate,
                16000,
                1,
                BUFSIZE
            );
            
            // 获取新的麦克风流
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            micStreamRef.current = stream;
            
            console.log('音频上下文初始化成功，采样率:', audioContextRef.current.sampleRate);
            return true;
        } catch (err) {
            setError(`初始化音频失败: ${err.message}`);
            onError?.(err);
            return false;
        }
    }, [onError]);
    
    // 音频处理回调
    const processAudioCallback = useCallback((e) => {
      console.log('processAudioCallback', e);
      if (!resamplerRef.current) return;
      
      const inputData = e.inputBuffer.getChannelData(0);
      
      // 计算音量级别
      let sum = 0;
      for (let i = 0; i < inputData.length; i++) {
          sum += inputData[i] * inputData[i];
      }
      const rms = Math.sqrt(sum / inputData.length);
      const volumeLevel = Math.round(rms * 1000);
      
      // 调用音量变化回调
      onVolumeChange?.(volumeLevel);
      
      const resampled = resamplerRef.current.resample(inputData);
      
      bufferArrayRef.current.push(...resampled);
      
      if (bufferArrayRef.current.length > 0 && wsRef.current?.readyState === 1) {
          const dataLength = bufferArrayRef.current.length * 2;
          const buffer = new ArrayBuffer(dataLength);
          const data = new DataView(buffer);
          let offset = 0;
          
          for (let i = 0; i < bufferArrayRef.current.length; i++, offset += 2) {
              const s = Math.max(-1, Math.min(1, bufferArrayRef.current[i]));
              data.setInt16(
                  offset,
                  s < 0 ? s * 0x8000 : s * 0x7fff,
                  true
              );
          }
          
          wsRef.current.send(buffer);
          bufferArrayRef.current = [];
      }
    }, [onVolumeChange]);
    
    // 连接WebSocket
    const connectWebSocket = useCallback(() => {
        const wsUrl = `${serverAPI.replace("https", "wss")}/ws/asr?lan=${_lan.current}`;
        
        wsRef.current = new WebSocket(wsUrl);
        
        wsRef.current.onopen = () => {
            connectSocketRef.current = true;
        };
        
        wsRef.current.onmessage = (evt) => {
            try {
                const data = JSON.parse(JSON.parse(evt.data));
                if (data.err_msg === "OK" && data.result) {
                    const reg = /[.,:!?;$#。，：！？；、]$/;
                    let processedResult = data.result;
                    
                    if (processedResult[processedResult.length - 1]?.match(reg)) {
                        processedResult = processedResult.slice(0, -1);
                    }
                    console.log(processedResult, 'processedResult')
                    onResult?.(processedResult);
                }
            } catch (err) {
                console.error('解析WebSocket消息失败:', err);
            }
        };
        
        wsRef.current.onerror = () => {
            if (connectSocketRef.current) {
                setTimeout(() => {
                    connectWebSocket();
                }, 1000);
            }
        };
        
        wsRef.current.onclose = () => {
            if (connectSocketRef.current) {
                setTimeout(() => {
                    connectWebSocket();
                }, 1000);
            }
        };
    }, [onResult]);
    // 停止识别
    const stopRecognition = useCallback(() => {
        console.log('stopRecognition called, isListening:', isListeningRef.current);
        
        if (!isListeningRef.current) {
            console.log('stopRecognition: 当前未在监听，直接返回');
            return;
        }
        
        try {
            connectSocketRef.current = false;
            
            // 关闭 WebSocket
            if (wsRef.current) {
                wsRef.current.close();
                wsRef.current = null;
            }
            
            // 清理音频节点
            if (scriptNodeRef.current) {
                scriptNodeRef.current.removeEventListener("audioprocess", processAudioCallback);
                scriptNodeRef.current.disconnect();
                scriptNodeRef.current = null;
            }
            
            if (sourceNodeRef.current) {
                sourceNodeRef.current.disconnect();
                sourceNodeRef.current = null;
            }
            
            // 停止麦克风流
            if (micStreamRef.current) {
                micStreamRef.current.getTracks().forEach(track => track.stop());
                micStreamRef.current = null;
            }
            
            // 清理音频上下文 - 关键修复
            if (audioContextRef.current) {
                if (audioContextRef.current.state !== 'closed') {
                    audioContextRef.current.close();
                }
                audioContextRef.current = null;
            }
            
            // 清理重采样器
            resamplerRef.current = null;
            
            // 清理定时器
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            
            setIsListening(false);
            bufferArrayRef.current = [];
            
            console.trace('停止识别完成，所有资源已清理');
            
        } catch (err) {
            setError(`停止识别失败: ${err.message}`);
            onError?.(err);
        }
    }, [processAudioCallback, onError]);

    // 检查是否可用
    const checkAvailable = useCallback(async ({lan, during}) => {
        try {
            // 构建参数 - 使用 FormData 发送文件
            const formData = new FormData();
            formData.append('platform', 'lab');
            formData.append('recognize_type', 2); // 实时识别
            formData.append('lan', lan);
            formData.append('during', during);
            formData.append('extension_id', 'cxspeech2Text');
            console.log('实时识别结果，调用语音识别接口:', formData);
            // 调用语音识别接口（不处理返回结果）
            const res = await speech2Recognition(formData)
            console.log('speech2Recognition实时识别结果:', res)
            if (res.success) {
                isAvailable.current = true
            } else {
                isAvailable.current = false
            }
            onThen?.(res)
        } catch(error) {
            isAvailable.current = false
            console.error('speech2Recognition实时识别失败结果:', error);
            onCatch?.(error)
        }
    }, [onThen, onCatch])

    // 开始识别
    const startRealTimeRecognition = useCallback(async ({during = 1, lan = ''}) => {
        if (isListeningRef.current) {
            console.log('startRealTimeRecognition: 当前已在监听，直接返回');
            return;
        }
        
        try {
            await checkAvailable({lan, during})
            if (!isAvailable.current) return;
            setError(null);
            console.log('startRealTimeRecognition: 开始启动识别');
            setIsListening(true);
            
            const audioInitialized = await initAudioContext();
            if (!audioInitialized) return;
            _during.current = during
            _lan.current = lan
            
            connectWebSocket();
            
            // 确保音频上下文处于运行状态
            if (audioContextRef.current.state === 'suspended') {
                await audioContextRef.current.resume();
            }
            
            // 创建音频节点
            sourceNodeRef.current = audioContextRef.current.createMediaStreamSource(micStreamRef.current);
            scriptNodeRef.current = audioContextRef.current.createScriptProcessor(BUFSIZE, 1, 1);
            
            // 连接音频节点
            sourceNodeRef.current.connect(scriptNodeRef.current);
            scriptNodeRef.current.connect(audioContextRef.current.destination);
            
            // 添加音频处理事件监听器
            scriptNodeRef.current.addEventListener("audioprocess", processAudioCallback);
            
            console.log('音频节点创建并连接成功', during);
            timeoutRef.current = setTimeout(() => {
              console.log('超时')
                stopRecognition();
            }, during * 1000);
            
        } catch (err) {
            setError(`开始识别失败: ${err.message}`);
            onError?.(err);
            setIsListening(false);
        }
    }, [initAudioContext, connectWebSocket, processAudioCallback, onError, stopRecognition, checkAvailable]);
    

    
    // 清理资源
    useEffect(() => {
        return () => {
            // stopRecognition();
        };
    }, [stopRecognition]);
    
    return {
        isListening,
        error,
        startRealTimeRecognition,
        stopRecognition,
    };
};
